import { useState } from "react";
import { NavLink } from "@/components/NavLink";
import { useLocation, useNavigate } from "react-router-dom";
import { useRestaurantContext } from "@/contexts/RestaurantContext";
import { useAuth } from "@/hooks/useAuth";
import { useUserRole } from "@/hooks/useUserRole";
import { getGreeting, getUserDisplayName } from "@/lib/greetings";
import {
  LayoutDashboard,
  FolderTree,
  Package,
  ShoppingCart,
  ClipboardList,
  BarChart3,
  Settings,
  LogOut,
  ChevronDown,
  Wallet,
  MapPin,
  Landmark,
  Grid3X3,
  Receipt,
  Users,
  Eye,
  History,
  Boxes,
  Calculator,
  PieChart,
  Smartphone,
  Ticket,
  Printer,
  XCircle,
  HelpCircle,
  Plus,
  Layers,
  Star,
  AlertTriangle,
  FileSpreadsheet,
  GitBranch,
  FileText,
  Truck,
} from "lucide-react";

import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  useSidebar,
} from "@/components/ui/sidebar";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { cn } from "@/lib/utils";

const mainMenuItems = [
  { title: "Dashboard", url: "/dashboard", icon: LayoutDashboard },
  { title: "Categorias", url: "/dashboard/categorias", icon: FolderTree },
  { title: "Produtos", url: "/dashboard/produtos", icon: Package },
  { title: "Adicionais", url: "/dashboard/adicionais", icon: Plus },
  { title: "Combos", url: "/dashboard/combos", icon: Layers },
  { title: "Estoque", url: "/dashboard/estoque", icon: Boxes },
  { title: "Mesas", url: "/dashboard/mesas", icon: Grid3X3 },
  { title: "Comandas", url: "/dashboard/comandas", icon: Receipt },
  { title: "Clientes", url: "/dashboard/clientes", icon: Users },
];

const salesMenuItems = [
  { title: "PDV", url: "/dashboard/pdv", icon: ShoppingCart },
  { title: "Delivery", url: "/dashboard/delivery", icon: Truck },
  { title: "Garçom", url: "/dashboard/garcom", icon: Smartphone },
  { title: "Abertura Comandas", url: "/dashboard/abertura-comandas", icon: Receipt },
  { title: "Consumo Aberto", url: "/dashboard/consumo-aberto", icon: Eye },
  { title: "Caixa", url: "/dashboard/caixa", icon: Landmark },
  { title: "Despesas", url: "/dashboard/despesas", icon: Wallet },
  { title: "Fornecedores", url: "/dashboard/fornecedores", icon: Users },
  { title: "Pedidos", url: "/dashboard/pedidos", icon: ClipboardList },
];

const reportMenuItems = [
  { title: "Vendas", url: "/dashboard/relatorios", icon: BarChart3 },
  { title: "DRE", url: "/dashboard/dre", icon: FileSpreadsheet },
  { title: "Combos", url: "/dashboard/relatorio-combos", icon: Layers },
  { title: "Ticket Médio", url: "/dashboard/ticket-medio", icon: Users },
  { title: "Entradas", url: "/dashboard/relatorio-entradas", icon: Ticket },
  { title: "Cancelamentos", url: "/dashboard/cancelamentos", icon: XCircle },
  { title: "Avaliações", url: "/dashboard/avaliacoes", icon: Star },
  { title: "CMV", url: "/dashboard/cmv", icon: Calculator },
  { title: "Margens", url: "/dashboard/margens", icon: PieChart },
];

const reportUrls = reportMenuItems.map(item => item.url);

const configMenuItems = [
  { title: "Plano de Contas", url: "/dashboard/plano-contas", icon: GitBranch },
  { title: "Categorias Despesa", url: "/dashboard/categorias-despesa", icon: FolderTree },
  { title: "Equipe", url: "/dashboard/equipe", icon: Users },
  { title: "Logs", url: "/dashboard/logs", icon: History },
  { title: "Erros", url: "/dashboard/erros", icon: AlertTriangle },
  { title: "Locais de Produção", url: "/dashboard/locais-producao", icon: MapPin },
  { title: "Impressoras", url: "/dashboard/impressoras", icon: Printer },
  { title: "Formas de Pagamento", url: "/dashboard/formas-pagamento", icon: Wallet },
  { title: "Fiscal / NFC-e", url: "/dashboard/fiscal", icon: FileText },
  { title: "Configurações", url: "/dashboard/configuracoes", icon: Settings },
  { title: "Ajuda", url: "/dashboard/ajuda", icon: HelpCircle },
];

// Items accessible to waiters only
const waiterAllowedUrls = ["/dashboard/garcom", "/dashboard/pdv"];

// Items accessible to cashiers (PDV, Caixa, basic sales)
const cashierAllowedUrls = [
  "/dashboard/pdv",
  "/dashboard/delivery",
  "/dashboard/garcom",
  "/dashboard/abertura-comandas",
  "/dashboard/consumo-aberto",
  "/dashboard/caixa",
  "/dashboard/despesas",
  "/dashboard/pedidos",
];

export function AppSidebar() {
  const location = useLocation();
  const navigate = useNavigate();
  const { state } = useSidebar();
  const { activeRestaurant } = useRestaurantContext();
  const { signOut, user } = useAuth();
  const { isWaiter, isCashier, canAccessFullDashboard, isLoading: roleLoading } = useUserRole();
  
  const collapsed = state === "collapsed";
  const currentPath = location.pathname;

  // Check if any report page is active
  const isReportActive = reportUrls.some(url => currentPath.startsWith(url));
  const [reportsOpen, setReportsOpen] = useState(isReportActive);

  const isActive = (path: string) => {
    if (path === "/dashboard") {
      return currentPath === "/dashboard";
    }
    return currentPath.startsWith(path);
  };

  const handleSignOut = async () => {
    await signOut();
    navigate("/auth");
  };

  // Filter menu items based on role
  const getFilteredMainMenuItems = () => {
    if (isWaiter) return [];
    if (isCashier) return [];
    return mainMenuItems;
  };

  const getFilteredSalesMenuItems = () => {
    if (isWaiter) {
      return salesMenuItems.filter(item => waiterAllowedUrls.includes(item.url));
    }
    if (isCashier) {
      return salesMenuItems.filter(item => cashierAllowedUrls.includes(item.url));
    }
    return salesMenuItems;
  };

  const getFilteredConfigMenuItems = () => {
    if (isWaiter || isCashier) return [];
    return configMenuItems;
  };

  const filteredMainMenu = getFilteredMainMenuItems();
  const filteredSalesMenu = getFilteredSalesMenuItems();
  const filteredConfigMenu = getFilteredConfigMenuItems();

  return (
    <Sidebar collapsible="icon" className="border-r border-sidebar-border">
      {/* Header */}
      <SidebarHeader className="p-3">
        <div className={cn(
          "flex items-center gap-3 rounded-lg bg-sidebar-accent/50 p-2 transition-all",
          collapsed && "justify-center p-1.5"
        )}>
          <div className="flex-shrink-0 h-14 w-14 rounded-lg bg-background/80 flex items-center justify-center overflow-hidden shadow-sm">
            {activeRestaurant?.logo_url ? (
              <img
                src={activeRestaurant.logo_url}
                alt={activeRestaurant.name}
                className="h-12 w-12 object-contain"
              />
            ) : (
              <img
                src="/logo-altus.png"
                alt="Altus Food"
                className="h-12 w-12 object-contain"
              />
            )}
          </div>
          {!collapsed && (
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-sidebar-foreground truncate">
                {getGreeting()}, {getUserDisplayName(user)}!
              </p>
              <p className="text-xs text-sidebar-foreground/60 truncate">
                {activeRestaurant?.name || "Altus Food"}
              </p>
            </div>
          )}
        </div>
      </SidebarHeader>

      <Separator className="bg-sidebar-border" />

      <SidebarContent className="px-2">
        {/* Menu Principal - Only for managers/owners */}
        {filteredMainMenu.length > 0 && (
          <SidebarGroup>
            <SidebarGroupLabel className="text-sidebar-foreground/50 text-xs uppercase tracking-wider">
              {!collapsed && "Menu"}
            </SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                {filteredMainMenu.map((item) => (
                  <SidebarMenuItem key={item.title}>
                    <SidebarMenuButton
                      asChild
                      isActive={isActive(item.url)}
                      tooltip={item.title}
                    >
                      <NavLink
                        to={item.url}
                        end={item.url === "/dashboard"}
                        className="flex items-center gap-3"
                        activeClassName="bg-sidebar-accent text-sidebar-accent-foreground"
                      >
                        <item.icon className="h-4 w-4 flex-shrink-0" />
                        {!collapsed && <span>{item.title}</span>}
                      </NavLink>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                ))}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        )}

        {/* Vendas */}
        {filteredSalesMenu.length > 0 && (
          <SidebarGroup>
            <SidebarGroupLabel className="text-sidebar-foreground/50 text-xs uppercase tracking-wider">
              {!collapsed && "Vendas"}
            </SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                {filteredSalesMenu.map((item) => (
                  <SidebarMenuItem key={item.title}>
                    <SidebarMenuButton
                      asChild
                      isActive={isActive(item.url)}
                      tooltip={item.title}
                    >
                      <NavLink
                        to={item.url}
                        className="flex items-center gap-3"
                        activeClassName="bg-sidebar-accent text-sidebar-accent-foreground"
                      >
                        <item.icon className="h-4 w-4 flex-shrink-0" />
                        {!collapsed && <span>{item.title}</span>}
                      </NavLink>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                ))}

                {/* Relatórios Submenu - Only for managers/owners */}
                {canAccessFullDashboard && (
                  <Collapsible open={reportsOpen || isReportActive} onOpenChange={setReportsOpen}>
                    <SidebarMenuItem>
                      <CollapsibleTrigger asChild>
                        <SidebarMenuButton
                          className="w-full"
                          isActive={isReportActive}
                          tooltip="Relatórios"
                        >
                          <BarChart3 className="h-4 w-4 flex-shrink-0" />
                          {!collapsed && (
                            <>
                              <span className="flex-1">Relatórios</span>
                              <ChevronDown className={cn(
                                "h-4 w-4 transition-transform duration-200",
                                (reportsOpen || isReportActive) && "rotate-180"
                              )} />
                            </>
                          )}
                        </SidebarMenuButton>
                      </CollapsibleTrigger>
                    </SidebarMenuItem>
                    <CollapsibleContent>
                      <SidebarMenuSub>
                        {reportMenuItems.map((item) => (
                          <SidebarMenuSubItem key={item.url}>
                            <SidebarMenuSubButton
                              asChild
                              isActive={isActive(item.url)}
                            >
                              <NavLink
                                to={item.url}
                                className="flex items-center gap-2"
                                activeClassName="bg-sidebar-accent text-sidebar-accent-foreground"
                              >
                                <item.icon className="h-3.5 w-3.5 flex-shrink-0" />
                                <span>{item.title}</span>
                              </NavLink>
                            </SidebarMenuSubButton>
                          </SidebarMenuSubItem>
                        ))}
                      </SidebarMenuSub>
                    </CollapsibleContent>
                  </Collapsible>
                )}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        )}

        {/* Configurações - Only for managers/owners */}
        {filteredConfigMenu.length > 0 && (
          <SidebarGroup>
            <SidebarGroupLabel className="text-sidebar-foreground/50 text-xs uppercase tracking-wider">
              {!collapsed && "Sistema"}
            </SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                {filteredConfigMenu.map((item) => (
                  <SidebarMenuItem key={item.title}>
                    <SidebarMenuButton
                      asChild
                      isActive={isActive(item.url)}
                      tooltip={item.title}
                    >
                      <NavLink
                        to={item.url}
                        className="flex items-center gap-3"
                        activeClassName="bg-sidebar-accent text-sidebar-accent-foreground"
                      >
                        <item.icon className="h-4 w-4 flex-shrink-0" />
                        {!collapsed && <span>{item.title}</span>}
                      </NavLink>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                ))}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        )}
      </SidebarContent>

      {/* Footer */}
      <SidebarFooter className="p-2">
        <Button
          variant="ghost"
          size={collapsed ? "icon" : "default"}
          onClick={handleSignOut}
          className="w-full justify-start text-sidebar-foreground/70 hover:text-sidebar-foreground hover:bg-sidebar-accent"
        >
          <LogOut className="h-4 w-4 flex-shrink-0" />
          {!collapsed && <span className="ml-3">Sair</span>}
        </Button>
      </SidebarFooter>
    </Sidebar>
  );
}
